/**
 * Build-time feature flags (GATSBY_* env vars are inlined at build).
 *
 * These exist because the sign-up funnel's shape changes with events and
 * PM-fitting efforts — we want to flip modes without code changes. Flipping a
 * flag today means setting the env var and redeploying (deploys are quick);
 * if we outgrow that, this module is the seam where a runtime flag service
 * would slot in.
 */

/**
 * Whether a spot-api base URL is available to this build. In development the
 * localhost default always works; a production build needs GATSBY_SPOT_API_URL
 * set (pending the hosting decision). Everything signup-flow-shaped should
 * degrade through this flag rather than shipping a wizard pointed at localhost.
 */
export const spotApiConfigured: boolean =
  process.env.NODE_ENV !== "production" || Boolean(process.env.GATSBY_SPOT_API_URL)

/**
 * What the header "Enable Your Portfolio" CTA does.
 * - "signup": goes straight to the /signup flow — no chooser. Current default
 *   (only the new flow is enabled; per Tim, 2026-09-06). Falls back to
 *   "legacy" automatically when spot-api isn't configured for this build.
 * - "choice": opens a chooser modal — Sign Up (the /signup flow) or Learn More
 *   (the existing lead form).
 * - "legacy": opens the existing lead form modal directly (pre-signup-flow
 *   behavior).
 */
export type SignupCtaMode = "signup" | "choice" | "legacy"
const rawCtaMode = process.env.GATSBY_SIGNUP_CTA_MODE
export const signupCtaMode: SignupCtaMode =
  rawCtaMode === "legacy" || rawCtaMode === "choice" ? rawCtaMode : "signup"

/**
 * What the "I don't use AppFolio" button in the sign-up wizard does.
 * - "manual": stays in the flow with manual portfolio entry (default — every
 *   interested lead completes, and non-AppFolio leads are market signal).
 * - "learn_more": exits the wizard to the Learn More path (the existing lead
 *   form) — for periods where we only want AppFolio-connected sign-ups.
 */
export type NoAppfolioMode = "manual" | "learn_more"
export const noAppfolioMode: NoAppfolioMode =
  process.env.GATSBY_SIGNUP_NO_APPFOLIO_MODE === "learn_more" ? "learn_more" : "manual"

/**
 * HubSpot Meetings scheduler URL shown on the sign-up confirmation step so a
 * completed lead can book a call immediately. Spot's portal is 50962400
 * (region na2); set this to a PM-facing meeting link, e.g.
 * https://meetings-na2.hubspot.com/<user>/<slug>. When unset, the confirmation
 * step keeps the "our team will reach out" copy — so it's safe to ship before
 * the meeting link is decided.
 */
export const hubspotMeetingUrl: string = process.env.GATSBY_HUBSPOT_MEETING_URL ?? ""

/**
 * Whether the internal-only demo surfaces (`/future/playbook` and
 * `/dashboard/demo-loading`) render at all.
 *
 * Nothing on the public site links to them, so today they are contained by
 * convention rather than by anything enforceable — a guessed URL reaches them.
 * In development they are always on. In a production build they render a
 * holding card unless `GATSBY_INTERNAL_SURFACES` is explicitly "true", which
 * is how a booth or event build turns them back on deliberately.
 *
 * Deliberately NOT gated: `/future/demo`, `/future/connect` and
 * `/appfolio/redirect-signup` are entry points that outsiders and AppFolio
 * itself reach, `/future/diagnostic` is an indexable brochure page, and
 * `/future/demo-tos` has to be readable by anyone the funnel asks to agree to
 * it. `/admin` left this list on 2026-09-26: it authenticates on its own, and
 * gating it as well only made the console unreachable in production.
 */
export const internalSurfacesEnabled: boolean =
  process.env.NODE_ENV !== "production" || process.env.GATSBY_INTERNAL_SURFACES === "true"

/**
 * The origin to bake into anything that outlives the page that generated it —
 * QR codes above all.
 *
 * A QR is built client-side, so one generated on a laptop preview encodes
 * `http://localhost:9000` and is worthless the moment it leaves that laptop.
 * In production builds this is the canonical site origin; in development it
 * stays empty so callers fall back to `window.location.origin` and local
 * testing keeps working. The admin launchpad's `?base=` override still wins
 * over both — that is the deliberate escape hatch for pointing booth codes at
 * a preview deploy.
 */
export const publicBaseUrl: string =
  process.env.GATSBY_PUBLIC_BASE_URL ??
  (process.env.NODE_ENV === "production" ? "https://spotrent.co" : "")
